/**
 * Inicia sesión demo local (sin backend) con datos del dueño precargados.
 * Usado por /demo y botones ADMIN DEMO en login.
 */

import type { AuthResponse, Permission, User } from "@/types";
import { txReadRaw, txWriteRaw } from "@/lib/persistence/transactionalStore";

export type DemoRole = "admin" | "employee";

const DEMO_BUSINESS: AuthResponse["business"] = {
  id: "demo-business",
  name: "Tienda Demo ALTENOT",
  type: "tienda",
  planType: "profesional",
  subscriptionStatus: "trial",
  trialEndsAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1_000),
  createdAt: new Date(),
};

const ADMIN_PERMISSIONS: Permission = {
  dashboard: "edit",
  caja: "edit",
  caja_ventas: "edit",
  caja_cierres: "edit",
  caja_movimientos: "edit",
  caja_creditos: "edit",
  caja_fidelizacion: "edit",
  inventario: "edit",
  personal: "edit",
  personal_tareas: "edit",
  personal_asistencia: "edit",
  personal_nomina: "edit",
  personal_calendario: "edit",
  personal_vacantes: "edit",
  personal_buzon: "edit",
  higiene: "edit",
  auditoria: "edit",
  metricas: "edit",
  ia: "edit",
  tienda: "edit",
  tienda_liquidaciones: "edit",
  tienda_configuracion: "edit",
  calificaciones: "edit",
  mesas: "edit",
  mesas_configuracion: "edit",
  cocina: "edit",
};

const EMPLOYEE_PERMISSIONS: Permission = {
  dashboard: "none",
  caja: "none",
  caja_ventas: "none",
  caja_cierres: "none",
  caja_movimientos: "none",
  caja_creditos: "none",
  caja_fidelizacion: "none",
  inventario: "none",
  personal: "none",
  personal_tareas: "none",
  personal_asistencia: "none",
  personal_nomina: "none",
  personal_calendario: "none",
  personal_vacantes: "none",
  personal_buzon: "none",
  higiene: "none",
  auditoria: "none",
  metricas: "none",
  ia: "none",
  tienda: "none",
  tienda_liquidaciones: "none",
  tienda_configuracion: "none",
  calificaciones: "none",
  mesas: "none",
  mesas_configuracion: "none",
  cocina: "view",
};

const DEFAULT_ADMIN: User = {
  id: "emp_1",
  email: "juan.perez@altenot.com",
  nombre: "Juan Pérez",
  role: "dueño",
  businessId: "demo-business",
  isActive: true,
  createdAt: new Date(),
  lastLogin: new Date(),
  permissions: ADMIN_PERMISSIONS,
};

const DEFAULT_EMPLOYEE: User = {
  id: "emp_3",
  email: "carlos.ruiz@altenot.com",
  nombre: "Carlos Ruiz",
  role: "empleado",
  businessId: "demo-business",
  isActive: true,
  createdAt: new Date(),
  lastLogin: new Date(),
  permissions: EMPLOYEE_PERMISSIONS,
};

function readStoredEmployee(id: string): User | null {
  try {
    const stored = txReadRaw("personal_empleados");
    const allEmps: Array<Record<string, unknown>> = stored
      ? JSON.parse(stored)
      : [];
    const emp = allEmps.find((e) => e.id === id);
    if (!emp || typeof emp.email !== "string") return null;

    const base: User = {
      id: String(emp.id),
      email: emp.email,
      nombre: typeof emp.nombre === "string" ? emp.nombre : undefined,
      businessId: "demo-business",
      isActive: true,
      createdAt: new Date(),
      lastLogin: new Date(),
      role: "empleado",
      permissions: id === "emp_1" ? ADMIN_PERMISSIONS : EMPLOYEE_PERMISSIONS,
    };

    if (id === "emp_1") {
      return { ...base, role: "dueño", permissions: ADMIN_PERMISSIONS };
    }
    // Leer permisos reales del empleado si existen en personal_empleados.
    // Sin esto, el botón "EMPLEADO DEMO" siempre arranca con EMPLOYEE_PERMISSIONS
    // y el sidebar no refleja los cambios del admin hasta el próximo ciclo de polling.
    const storedPermissions =
      emp.permissions &&
      typeof emp.permissions === "object" &&
      !Array.isArray(emp.permissions)
        ? (emp.permissions as Permission)
        : null;
    return {
      ...base,
      role: (emp.rol as User["role"]) ?? "empleado",
      permissions: storedPermissions ?? EMPLOYEE_PERMISSIONS,
    };
  } catch {
    return null;
  }
}

export function buildDemoUser(role: DemoRole = "admin"): User {
  if (role === "employee") {
    return readStoredEmployee("emp_3") ?? DEFAULT_EMPLOYEE;
  }
  return readStoredEmployee("emp_1") ?? DEFAULT_ADMIN;
}

export function startDemoSession(role: DemoRole = "admin"): AuthResponse {
  const user = buildDemoUser(role);
  const token =
    role === "employee" ? "mock-token-employee" : "mock-token-admin";
  txWriteRaw("altenot_session_user", JSON.stringify(user));
  txWriteRaw("altenot_demo_mode", "true");
  return { user, token, business: DEMO_BUSINESS };
}

export function isDemoSession(token: string | null | undefined): boolean {
  return !!token && token.startsWith("mock-token");
}
